<template>
  <SoPage>
    <div
      class="account-not-found d-flex flex-column align-center text-center"
      data-testid="account-not-found"
    >
      <div class="account-not-found__scene" aria-hidden="true">
        <span
          v-for="i in 8"
          :key="i"
          class="account-not-found__fume"
          :style="'--i:' + i"
        ></span>
        <div class="account-not-found__rocket">
          <SoIcon icon="rocket" size="x-large" />
        </div>
        <svg
          class="account-not-found__asteroid"
          viewBox="0 0 120 120"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            class="account-not-found__asteroid-body"
            d="M62 6c14-3 30 6 38 17 9 12 16 27 12 42-3 14-16 25-28 33-13 9-30 14-45 8C22 100 8 86 6 68 4 52 11 36 23 24 34 13 48 9 62 6Z"
          />
          <ellipse
            class="account-not-found__crater"
            cx="44"
            cy="42"
            rx="12"
            ry="9"
          />
          <ellipse
            class="account-not-found__crater"
            cx="80"
            cy="62"
            rx="8"
            ry="6"
          />
          <ellipse
            class="account-not-found__crater"
            cx="52"
            cy="80"
            rx="6"
            ry="5"
          />
        </svg>
        <span
          v-for="i in 6"
          :key="'shard-' + i"
          class="account-not-found__shard"
          :style="'--i:' + i"
        ></span>
      </div>

      <p class="text-h2 font-weight-regular text-medium-emphasis mt-6 mb-3">
        {{ accountNumber }}
      </p>
      <h1 class="text-h2">Account Not Found</h1>
      <p class="text-body-1 mt-3">
        Check the number for typos or check you selected the correct search term
        from the dropdown
      </p>
    </div>
  </SoPage>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoPage from "@/components/templates/SoPage.vue"
  import SoIcon from "@/components/SoIcon.vue"

  export default defineComponent({
    components: { SoPage, SoIcon },
    props: {
      accountNumber: {
        type: String,
        required: true,
      },
    },
  })
</script>

<style scoped>
  .account-not-found {
    --flight-path: path("M 4 95 C 12 15 48 30 83 50");
    --flight: 0.75s;
    --flight-easing: cubic-bezier(0.4, 0.1, 0.8, 0.7);
    padding-top: 10vh;
  }

  .account-not-found__scene {
    position: relative;
    width: 160px;
    height: 105px;
  }

  .account-not-found__asteroid {
    position: absolute;
    left: 80px;
    top: 40px;
    width: 40px;
    height: 40px;
    animation: asteroid-shake 0.2s ease-in-out var(--flight) 1;
  }

  .account-not-found__asteroid-body {
    fill: rgb(var(--v-theme-surface-dark));
    stroke: rgb(var(--v-theme-on-surface));
    stroke-opacity: 0.2;
    stroke-width: 3;
  }

  .account-not-found__crater {
    fill: rgb(var(--v-theme-on-surface));
    fill-opacity: 0.12;
  }

  .account-not-found__rocket {
    position: absolute;
    left: 0;
    top: 0;
    width: 32px;
    height: 32px;
    color: rgb(var(--v-theme-on-surface));
    offset-path: var(--flight-path);
    offset-rotate: auto 90deg;
    transform: scale(0.9);
    animation: rocket-fly var(--flight) var(--flight-easing) 1 forwards;
  }

  .account-not-found__fume {
    position: absolute;
    left: 0;
    top: 0;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background-color: rgb(var(--v-theme-secondary));
    offset-path: var(--flight-path);
    offset-rotate: 0deg;
    transform: scale(calc(1 - var(--i) * 0.1));
    animation: fume-fly var(--flight) var(--flight-easing)
      calc(var(--i) * 0.025s) 1 both;
  }

  .account-not-found__shard {
    position: absolute;
    left: 82px;
    top: 49px;
    width: 3px;
    height: 3px;
    border-radius: 1px;
    background-color: rgb(var(--v-theme-sun));
    opacity: 0;
    transform: rotate(calc(60deg * var(--i)));
    animation: shard-burst 0.25s ease-out var(--flight) 1 forwards;
  }

  .account-not-found__shard:nth-child(even) {
    background-color: rgb(var(--v-theme-fire));
  }

  @keyframes rocket-fly {
    from {
      offset-distance: 0%;
    }
    to {
      offset-distance: 100%;
    }
  }

  @keyframes fume-fly {
    0% {
      offset-distance: 0%;
      opacity: 0;
    }
    5% {
      opacity: 0.9;
    }
    90% {
      opacity: 0.5;
    }
    100% {
      offset-distance: 100%;
      opacity: 0;
    }
  }

  @keyframes asteroid-shake {
    0%,
    100% {
      translate: 0 0;
      rotate: 0deg;
    }
    25% {
      translate: 2px -1px;
      rotate: 2deg;
    }
    55% {
      translate: -1px 1px;
      rotate: -2deg;
    }
    80% {
      translate: 1px 0;
      rotate: 1deg;
    }
  }

  @keyframes shard-burst {
    0% {
      opacity: 1;
      translate: 0 0;
    }
    100% {
      opacity: 0;
      translate: calc(cos(60deg * var(--i)) * 22px)
        calc(sin(60deg * var(--i)) * -22px);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .account-not-found__asteroid,
    .account-not-found__rocket,
    .account-not-found__fume,
    .account-not-found__shard {
      animation: none;
    }

    .account-not-found__rocket {
      offset-distance: 100%;
    }

    .account-not-found__fume {
      offset-distance: calc(100% - var(--i) * 6%);
      opacity: calc(1 - var(--i) * 0.1);
    }
  }
</style>
